import React from 'react';
import { Shield, Lock, Eye } from 'lucide-react';
import { roleService } from '../../services/auth/RoleService';

/**
 * Role Management Page
 * Xem và quản lý roles & permissions trong hệ thống
 */
const RoleManagement: React.FC = () => {
  const allRoles = roleService.getAllRoles();
  const allPermissions = roleService.getAllPermissions();

  // Group permissions by resource
  const permissionsByResource = allPermissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {} as Record<string, typeof allPermissions>);

  const getRoleColor = (roleId: string) => {
    switch (roleId) {
      case 'admin': return 'border-red-500 bg-red-50';
      case 'teacher': return 'border-purple-500 bg-purple-50';
      case 'student': return 'border-green-500 bg-green-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Quản lý phân quyền</h1>
        <p className="text-gray-600 mt-2">Xem roles và permissions trong hệ thống</p>
      </div>

      {/* Roles Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {allRoles.map((role) => (
          <div
            key={role.id}
            className={`border-2 rounded-xl p-6 ${getRoleColor(role.id)}`}
          >
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-8 h-8" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">{role.name}</h3>
                <p className="text-sm text-gray-600">{role.description}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Số quyền:</span>
                <span className="font-semibold">{role.permissions.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">System Role:</span>
                <span className="font-semibold">{role.isSystemRole ? 'Có' : 'Không'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Permissions Matrix */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Ma trận phân quyền</h2>
          <p className="text-sm text-gray-600 mt-1">
            Xem quyền hạn của từng vai trò
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">
                  Permission
                </th>
                {allRoles.map((role) => (
                  <th
                    key={role.id}
                    className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase"
                  >
                    {role.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(permissionsByResource).map(([resource, permissions]) => (
                <React.Fragment key={resource}>
                  <tr className="bg-gray-100">
                    <td
                      colSpan={allRoles.length + 1}
                      className="px-6 py-3 text-sm font-bold text-gray-700 uppercase"
                    >
                      {resource}
                    </td>
                  </tr>
                  {permissions.map((permission) => (
                    <tr key={permission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {permission.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {permission.description}
                          </p>
                        </div>
                      </td>
                      {allRoles.map((role) => (
                        <td key={role.id} className="px-6 py-4 text-center">
                          {role.permissions.includes(permission.id) ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-green-500 text-white rounded-full">
                              ✓
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-200 text-gray-400 rounded-full">
                              −
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Eye className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              Chế độ xem chỉ đọc
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Hiện tại chỉ hỗ trợ xem thông tin phân quyền. Để thay đổi quyền của user, vui lòng sử dụng chức năng "Chỉnh sửa" trong trang Quản lý thành viên.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;

